import { effect, inject, Injectable, InjectionToken } from '@angular/core';
import { delay, Observable, of, throwError } from 'rxjs';

import { PartyCaseDraft, PartyCaseFeeActivity, PartyCaseFeeActivityHistory, PartyCaseListItem, PartyCaseOverview, PartyCaseRequestReceipt, PartyCheckoutSession, PartyDocument } from './party-case.models';
import { PaymentNotReadyError } from '../../core/http/payment-errors';
import { ReviewSessionService } from '../../core/review/review-session.service';
import { isValidMoney } from '../../core/review/review-validation';
import { ReviewScenarioService } from '../../core/review/review-scenario.service';
import { ReviewCaseRepository } from '../../core/review/review-case.repository';

export interface PartyCaseService {
  listCases(): Observable<readonly PartyCaseListItem[]>;
  getCase(caseNumber: string): Observable<PartyCaseOverview | null>;
  getCaseFeeActivity(caseNumber: string): Observable<PartyCaseFeeActivityHistory>;
  saveDraft(caseNumber: string, draft: PartyCaseDraft, expectedRevision?: number): Observable<PartyCaseOverview | null>;
  submitCase(caseNumber: string, expectedRevision?: number): Observable<PartyCaseOverview | null>;
  refreshCase(caseNumber: string): Observable<PartyCaseOverview | null>;
  /** Starts a ForthrightPayments checkout for the caller's own fee. Errors with PaymentNotReadyError or PaymentsUnavailableError. */
  startCheckout(caseNumber: string): Observable<PartyCheckoutSession>;
  /** Server-verified payment check: the API syncs the fee from ForthrightPayments and locks when it is paid in full. */
  refreshPayment(caseNumber: string, expectedRevision: number): Observable<PartyCaseOverview | null>;
  /** Review build only: the synthetic, client-reported payment result. The live API answers 404. */
  confirmReviewPayment(caseNumber: string, expectedRevision: number, state: 'paid' | 'pending' | 'election-pending' | 'canceled' | 'failed'): Observable<PartyCaseOverview | null>;
  saveInformationResponse(caseNumber: string, requestId: string, response: string, documents: readonly PartyDocument[], expectedRevision: number): Observable<PartyCaseOverview | null>;
  sendContact(caseNumber: string, subject: string, message: string): Observable<boolean>;
  submitCaseRequest(caseNumber: string, type: 'Settlement' | 'Withdrawal', note: string): Observable<PartyCaseOverview | null>;
  /** Streams a document's real bytes. Null when it does not exist or the caller may not see it. */
  openDocument(caseNumber: string, documentId: string): Observable<Blob | null>;
  /** Attaches one file. Returns the refreshed overview, so the document list reflects the upload. */
  uploadDocument(caseNumber: string, file: File, association?: DocumentAssociation): Observable<PartyCaseOverview | null>;
  /** Removes a document from an open draft. */
  deleteDocument(caseNumber: string, documentId: string): Observable<PartyCaseOverview | null>;
}

/** Which thing a document hangs off. Neither field means a case-level document. */
export interface DocumentAssociation {
  readonly lineItemId?: string;
  readonly caseRequestId?: string;
}

const mockCaseFeeActivity: readonly PartyCaseFeeActivity[] = [
  { effectiveDate: 'September 1, 2026', activity: 'Entity fee refund issued', partyPreposition: 'to', amount: '$475.00', amountSign: '+', status: 'Completed', methodDetail: null },
  { effectiveDate: 'August 25, 2026', activity: 'Case fee payment received', partyPreposition: 'from', amount: '$490.00', amountSign: '-', status: 'Completed', methodDetail: null },
  { effectiveDate: 'August 17, 2026', activity: 'Case fee obligation created', partyPreposition: 'for', amount: '$490.00', amountSign: null, status: 'Completed', methodDetail: null },
];

const initialMockCases: readonly PartyCaseOverview[] = [
  createCase({
    forthrightCaseNumber: 'NS100237',
    cmsDisputeNumber: 'CMS-2026-004821',
    counterpartyLegalName: 'Acme Health Plan',
    status: 'Action Required',
    nextRequiredAction: 'Submit your offer and pay fees',
    deadline: { state: 'Active', daysRemaining: 2, deadlineDateEastern: 'September 3, 2026 at 11:59 PM ET' },
    offerState: 'Draft',
    submittedLockedState: 'Not submitted',
    offerAmountOrTotal: '$2,900.00',
    paymentState: 'Payment required',
    totalDue: '$490.00',
    amountPaid: '$0.00',
    amountOutstanding: '$490.00',
    caseDocuments: [document('DOC-CASE-1', 'Case correspondence.pdf')],
  }),
  createCase({
    forthrightCaseNumber: 'NS100238',
    cmsDisputeNumber: 'CMS-2026-004836',
    counterpartyLegalName: 'Northstar Benefit Services',
    status: 'Submitted / Payment Pending',
    nextRequiredAction: 'Payment is processing',
    deadline: { state: 'Active', daysRemaining: 5, deadlineDateEastern: 'September 10, 2026 at 11:59 PM ET' },
    offerState: 'Submitted',
    submittedLockedState: 'Locked',
    offerAmountOrTotal: '$1,850.00',
    paymentState: 'Pending',
    totalDue: '$490.00',
    amountPaid: '$0.00',
    amountOutstanding: '$490.00',
    latestPaymentAmount: '$490.00',
    latestPaymentDate: 'September 1, 2026',
    latestPaymentState: 'Pending',
    paymentMethodOrReference: 'Check ending 1842',
  }),
  createCase({
    forthrightCaseNumber: 'NS100239',
    cmsDisputeNumber: 'CMS-2026-004851',
    counterpartyLegalName: 'Evergreen Insurance Company',
    status: 'Submitted & Paid',
    nextRequiredAction: null,
    deadline: { state: 'Complete', daysRemaining: null, deadlineDateEastern: 'Completed August 28, 2026 ET' },
    offerState: 'Submitted',
    submittedLockedState: 'Locked',
    offerAmountOrTotal: '$3,240.00',
    paymentState: 'Paid',
    totalDue: '$490.00',
    amountPaid: '$490.00',
    amountOutstanding: '$0.00',
    latestPaymentAmount: '$490.00',
    latestPaymentDate: 'August 27, 2026',
    latestPaymentState: 'Completed',
    paymentMethodOrReference: 'Card ending 4231',
  }),
  createCase({
    forthrightCaseNumber: 'NS100240',
    cmsDisputeNumber: 'CMS-2026-004879',
    counterpartyLegalName: 'Summit National Health',
    status: 'Information Requested',
    nextRequiredAction: 'Respond to Forthright’s information request',
    deadline: { state: 'Due today', daysRemaining: 0, deadlineDateEastern: 'September 1, 2026 at 11:59 PM ET' },
    offerState: 'Unavailable',
    submittedLockedState: 'Unavailable',
    offerAmountOrTotal: null,
    paymentState: 'Not yet due',
    totalDue: '$0.00',
    amountPaid: '$0.00',
    amountOutstanding: '$0.00',
  }),
  createCase({
    forthrightCaseNumber: 'NS100241',
    cmsDisputeNumber: 'CMS-2026-004901',
    counterpartyLegalName: 'Harborview Health Plan',
    status: 'Awaiting Determination',
    nextRequiredAction: null,
    deadline: { state: 'Active', daysRemaining: 9, deadlineDateEastern: 'September 15, 2026 at 11:59 PM ET' },
    offerState: 'Submitted',
    submittedLockedState: 'Locked',
    offerAmountOrTotal: '$7,475.00',
    paymentState: 'Paid',
    totalDue: '$790.00',
    amountPaid: '$790.00',
    amountOutstanding: '$0.00',
    latestPaymentAmount: '$790.00',
    latestPaymentDate: 'August 25, 2026',
    latestPaymentState: 'Completed',
    paymentMethodOrReference: 'Card ending 7710',
    isBatched: true,
    lineItems: [
      lineItem('LI-1', 'Emergency physician service', '99284', 'August 2, 2026', '$1,150.00', 'Submitted', '$2,900.00'),
      lineItem('LI-2', 'Emergency physician service', '99285', 'August 2, 2026', '$1,725.00', 'Submitted', '$4,575.00'),
    ],
  }),
  createCase({
    forthrightCaseNumber: 'NS100242',
    cmsDisputeNumber: 'CMS-2026-004934',
    counterpartyLegalName: 'Atlas Employer Health',
    status: 'Decided',
    nextRequiredAction: null,
    deadline: { state: 'Complete', daysRemaining: null, deadlineDateEastern: 'Determination sent August 31, 2026 ET' },
    offerState: 'Submitted',
    submittedLockedState: 'Locked',
    offerAmountOrTotal: '$2,450.00',
    paymentState: 'Paid',
    totalDue: '$490.00',
    amountPaid: '$490.00',
    amountOutstanding: '$0.00',
    latestPaymentAmount: '$490.00',
    latestPaymentDate: 'August 19, 2026',
    latestPaymentState: 'Completed',
    paymentMethodOrReference: 'Card ending 9120',
    refundState: 'Entity-fee refund pending',
    refundPendingAmount: '$475.00',
    determination: {
      sentDate: 'August 31, 2026',
      outcomeLabel: 'Your offer was selected',
      availabilityMessage: 'The reasoned determination is available for read-only review. Download is not available at this time.',
      body: 'After considering the submitted offers, the QPA, and the permitted case-specific information, the certified IDR entity selected your offer. This synthetic content is published independently for Party review.',
    },
  }),
  createCase({
    forthrightCaseNumber: 'NS100243',
    cmsDisputeNumber: 'CMS-2026-004952',
    counterpartyLegalName: 'Blue River Insurance',
    status: 'Settled',
    nextRequiredAction: null,
    deadline: { state: 'Not applicable', daysRemaining: null, deadlineDateEastern: 'No active deadline' },
    offerState: 'Draft closed',
    submittedLockedState: 'Closed',
    offerAmountOrTotal: '$1,975.00',
    paymentState: 'Refunded in part',
    totalDue: '$490.00',
    amountPaid: '$490.00',
    amountOutstanding: '$0.00',
    latestPaymentAmount: '$490.00',
    latestPaymentDate: 'August 11, 2026',
    latestPaymentState: 'Completed',
    paymentMethodOrReference: 'Card ending 1044',
    refundState: 'Half of the entity fee refunded',
  }),
  createCase({
    forthrightCaseNumber: 'NS100244',
    cmsDisputeNumber: 'CMS-2026-004963',
    counterpartyLegalName: 'Union State Health Plan',
    status: 'Withdrawn',
    nextRequiredAction: null,
    deadline: { state: 'Not applicable', daysRemaining: null, deadlineDateEastern: 'No active deadline' },
    offerState: 'Not submitted',
    submittedLockedState: 'Closed',
    offerAmountOrTotal: null,
    paymentState: 'No payment recorded',
    totalDue: '$0.00',
    amountPaid: '$0.00',
    amountOutstanding: '$0.00',
  }),
  createCase({
    forthrightCaseNumber: 'NS100245',
    cmsDisputeNumber: 'CMS-2026-004980',
    counterpartyLegalName: 'CivicCare Administrators',
    status: 'Closed / Ineligible',
    nextRequiredAction: null,
    deadline: { state: 'Not applicable', daysRemaining: null, deadlineDateEastern: 'No active deadline' },
    offerState: 'Not available',
    submittedLockedState: 'Closed',
    offerAmountOrTotal: null,
    paymentState: 'Administrative fee retained',
    totalDue: '$15.00',
    amountPaid: '$15.00',
    amountOutstanding: '$0.00',
    latestPaymentAmount: '$15.00',
    latestPaymentDate: 'August 7, 2026',
    latestPaymentState: 'Completed',
    paymentMethodOrReference: 'Card ending 4231',
  }),
  createCase({
    forthrightCaseNumber: 'NS100246',
    cmsDisputeNumber: 'CMS-2026-005002',
    counterpartyLegalName: 'Prairie Mutual Health',
    status: 'Draft (not submitted)',
    nextRequiredAction: 'Finish your offer',
    deadline: { state: 'Overdue', daysRemaining: null, deadlineDateEastern: 'August 31, 2026 at 11:59 PM ET' },
    offerState: 'Draft',
    submittedLockedState: 'Not submitted',
    offerAmountOrTotal: null,
    paymentState: 'Not started',
    totalDue: '$490.00',
    amountPaid: '$0.00',
    amountOutstanding: '$490.00',
  }),
];

function document(id: string, name: string): PartyDocument {
  return { id, name };
}

function lineItem(id: string, serviceDescription: string, serviceCode: string, serviceDate: string, qpa: string, ownOfferState: string, ownOfferAmount: string | null) {
  const ownDocuments = ownOfferAmount ? [document(`${id}-DOC-1`, `${serviceCode} support.pdf`)] : [];
  return { id, serviceDescription, serviceCode, serviceDate, qpa, ownOfferState, ownOfferAmount, ownDocuments } as const;
}

function createCase(input: {
  forthrightCaseNumber: string;
  cmsDisputeNumber: string;
  counterpartyLegalName: string;
  status: PartyCaseOverview['status'];
  nextRequiredAction: string | null;
  deadline: PartyCaseOverview['deadline'];
  offerState: string;
  submittedLockedState: string;
  offerAmountOrTotal: string | null;
  paymentState: string;
  totalDue: string;
  amountPaid: string;
  amountOutstanding: string;
  latestPaymentAmount?: string;
  latestPaymentDate?: string;
  latestPaymentState?: string;
  paymentMethodOrReference?: string;
  refundState?: string;
  refundPendingAmount?: string;
  caseDocuments?: readonly PartyDocument[];
  isBatched?: boolean;
  lineItems?: PartyCaseOverview['lineItems'];
  determination?: PartyCaseOverview['determination'];
}): PartyCaseOverview {
  const lineItems = input.lineItems ?? [lineItem('LI-1', 'Emergency physician service', '99284', 'August 2, 2026', '$1,150.00', input.offerState, input.offerAmountOrTotal)];

  return {
    forthrightCaseNumber: input.forthrightCaseNumber,
    cmsDisputeNumber: input.cmsDisputeNumber,
    organizationLegalName: 'Sunrise Emergency Physicians',
    counterpartyLegalName: input.counterpartyLegalName,
    status: input.status,
    nextRequiredAction: input.nextRequiredAction,
    deadline: input.deadline,
    serviceDescription: input.isBatched ? `${lineItems.length} emergency physician services` : lineItems[0].serviceDescription,
    serviceDateOrRange: 'August 2, 2026',
    serviceCodeSummary: input.isBatched ? lineItems.map((item) => item.serviceCode).join(', ') : lineItems[0].serviceCode,
    qpaSummary: input.isBatched ? `${lineItems.length} line-item QPAs` : lineItems[0].qpa,
    selectionDate: 'August 17, 2026',
    isBatched: input.isBatched ?? false,
    isEditable: input.submittedLockedState === 'Not submitted',
    isSubmitted: input.submittedLockedState === 'Locked',
    caseDocuments: input.caseDocuments ?? [],
    lineItems,
    accountBalance: input.refundPendingAmount
      ? { amount: input.refundPendingAmount, amountSign: '+', label: 'Refund pending' }
      : input.amountOutstanding !== '$0.00'
        ? { amount: input.amountOutstanding, amountSign: '-', label: 'Payment owed' }
        : { amount: '$0.00', amountSign: null, label: 'No balance' },
    offerPayment: {
      offerState: input.offerState,
      submittedLockedState: input.submittedLockedState,
      offerAmountOrTotal: input.offerAmountOrTotal,
      supportingDocuments: input.offerState === 'Unavailable' || input.offerState === 'Not available' ? 'Unavailable' : '2 documents',
      paymentState: input.paymentState,
      entityFee: input.totalDue === '$790.00' ? '$775.00' : input.totalDue === '$15.00' || input.totalDue === '$0.00' ? '$0.00' : '$475.00',
      administrativeFee: input.totalDue === '$0.00' ? '$0.00' : '$15.00',
      totalDue: input.totalDue,
      amountPaid: input.amountPaid,
      amountOutstanding: input.amountOutstanding,
      hasOutstandingBalance: input.amountOutstanding !== '$0.00',
      latestPaymentAmount: input.latestPaymentAmount ?? null,
      latestPaymentDate: input.latestPaymentDate ?? null,
      latestPaymentState: input.latestPaymentState ?? null,
      paymentMethodOrReference: input.paymentMethodOrReference ?? null,
      refundState: input.refundState ?? null,
    },
    determination: input.determination ?? null,
    determinationState: input.determination ? 'available' : input.status === 'Awaiting Determination' ? 'withheld' : 'unavailable',
    savedRevision: 1,
    offerLifecycle: input.submittedLockedState === 'Not submitted' ? 'editable' : input.submittedLockedState === 'Locked' ? 'submitted' : 'closed',
    capabilities: capabilities(
      input.submittedLockedState === 'Not submitted' ? 'editable' : input.submittedLockedState === 'Locked' ? 'submitted' : 'closed',
      input.paymentState,
      input.amountOutstanding,
    ),
    narrative: '',
    attributes: {},
    informationRequests:
      input.status === 'Information Requested'
        ? [
            {
              id: 'INFO-1',
              question: 'Please provide additional information about the service and the offer factors considered.',
              dueLabel: input.deadline.deadlineDateEastern,
              status: 'Open',
              response: '',
              documents: [],
            },
          ]
        : [],
    activity: [{ date: 'September 1, 2026', title: 'Case status updated', detail: input.status }],
    caseRequests: [],
  };
}

function capabilities(lifecycle: PartyCaseOverview['offerLifecycle'], paymentState: string, amountOutstanding: string): PartyCaseOverview['capabilities'] {
  const locked = lifecycle === 'locked';
  const submitted = lifecycle === 'submitted';
  const pending = paymentState === 'Pending';
  return {
    canEditOffer: lifecycle === 'editable',
    canPay: (lifecycle === 'editable' || submitted) && amountOutstanding !== '$0.00' && !pending,
    canCheckPayment: pending,
    canSubmit: locked,
    canRespond: true,
    canContact: true,
    canRequestClosure: !submitted,
    unavailableReason: lifecycle === 'closed' ? 'Case actions are closed.' : null,
  };
}

@Injectable({ providedIn: 'root' })
export class MockPartyCaseService implements PartyCaseService {
  private readonly session = inject(ReviewSessionService);
  private readonly scenarios = inject(ReviewScenarioService);
  private readonly reviewRepository = inject(ReviewCaseRepository);
  private cases = this.session.read<PartyCaseOverview[]>('party-org-sunrise', this.storageName()) ?? this.initialCasesForScenario();
  private activeScenarioId = this.scenarios.selectedId();
  private resetSeen = this.scenarios.resetSequence();

  constructor() {
    effect(() => {
      const scenarioId = this.scenarios.selectedId();
      const sequence = this.scenarios.resetSequence();
      if (scenarioId !== this.activeScenarioId) {
        this.activeScenarioId = scenarioId;
        this.cases = this.session.read<PartyCaseOverview[]>('party-org-sunrise', this.storageName()) ?? this.initialCasesForScenario();
      }
      if (sequence !== this.resetSeen) {
        this.resetSeen = sequence;
        this.cases = this.initialCasesForScenario();
        this.persist();
      }
    });
  }

  listCases(): Observable<readonly PartyCaseListItem[]> {
    if (this.scenarios.selectedId() === 'failures') return throwError(() => new Error('Synthetic Party list failure'));
    return of(
      this.cases.map(({ forthrightCaseNumber, cmsDisputeNumber, counterpartyLegalName, status, nextRequiredAction, deadline, offerPayment, accountBalance }) => ({
        forthrightCaseNumber,
        cmsDisputeNumber,
        counterpartyLegalName,
        status,
        nextRequiredAction,
        deadline: { ...deadline },
        offerPayment: { ...offerPayment },
        accountBalance: { ...accountBalance },
      })),
    ).pipe(delay(75));
  }

  getCase(caseNumber: string): Observable<PartyCaseOverview | null> {
    if (this.scenarios.selectedId() === 'failures') return throwError(() => new Error('Synthetic Party detail failure'));
    const matchingCase = this.cases.find((item) => item.forthrightCaseNumber === caseNumber.toUpperCase()) ?? null;
    return of(matchingCase ? cloneCase(matchingCase) : null).pipe(delay(75));
  }

  getCaseFeeActivity(caseNumber: string): Observable<PartyCaseFeeActivityHistory> {
    const item = this.cases.find((candidate) => candidate.forthrightCaseNumber === caseNumber.toUpperCase());
    if (!item) return of<PartyCaseFeeActivityHistory>({ entries: [], historyAvailability: 'full' }).pipe(delay(75));
    const activity: PartyCaseFeeActivity[] = [];
    if (item.offerPayment.refundState)
      activity.push({
        effectiveDate: 'September 1, 2026',
        activity: item.offerPayment.refundState,
        partyPreposition: 'to',
        amount: item.accountBalance.label === 'Refund pending' ? item.accountBalance.amount : '$237.50',
        amountSign: '+',
        status: item.accountBalance.label === 'Refund pending' ? 'Pending' : 'Completed',
        methodDetail: null,
      });
    if (item.offerPayment.amountPaid !== '$0.00')
      activity.push({
        effectiveDate: item.offerPayment.latestPaymentDate ?? 'August 25, 2026',
        activity: 'Case fee payment received',
        partyPreposition: 'from',
        amount: item.offerPayment.amountPaid,
        amountSign: '-',
        status: item.offerPayment.latestPaymentState ?? 'Completed',
        methodDetail: null,
      });
    if (item.offerPayment.totalDue !== '$0.00')
      activity.push({ effectiveDate: 'August 17, 2026', activity: 'Case fee obligation created', partyPreposition: 'for', amount: item.offerPayment.totalDue, amountSign: null, status: 'Completed', methodDetail: null });
    return of<PartyCaseFeeActivityHistory>({ entries: activity, historyAvailability: 'full' }).pipe(delay(75));
  }

  saveDraft(caseNumber: string, draft: PartyCaseDraft, expectedRevision?: number): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((item) => item.forthrightCaseNumber === caseNumber.toUpperCase());
    if (
      index < 0 ||
      !this.cases[index].capabilities.canEditOffer ||
      (expectedRevision !== undefined && expectedRevision !== this.cases[index].savedRevision) ||
      draft.lineItems.some((line) => !isValidMoney(line.offerAmount))
    ) {
      return of(null).pipe(delay(50));
    }

    const current = this.cases[index];
    const lineItems = current.lineItems.map((line) => {
      const update = draft.lineItems.find((item) => item.id === line.id);
      return update ? { ...line, ownOfferAmount: formatCurrency(update.offerAmount), ownOfferState: update.offerAmount === null ? 'Not entered' : 'Draft', ownDocuments: [...update.documents] } : line;
    });
    const offerTotal = draft.lineItems.reduce((total, line) => total + (line.offerAmount ?? 0), 0);
    const documentCount = draft.caseDocuments.length + draft.lineItems.reduce((total, line) => total + line.documents.length, 0);
    const updated: PartyCaseOverview = {
      ...current,
      lineItems,
      caseDocuments: [...draft.caseDocuments],
      offerPayment: {
        ...current.offerPayment,
        offerState: 'Draft',
        submittedLockedState: 'Not submitted',
        offerAmountOrTotal: offerTotal > 0 ? formatCurrency(offerTotal) : null,
        supportingDocuments: `${documentCount} ${documentCount === 1 ? 'document' : 'documents'}`,
      },
      savedRevision: current.savedRevision + 1,
      narrative: draft.narrative ?? current.narrative,
      attributes: { ...(draft.attributes ?? current.attributes) },
      activity: [{ date: 'September 14, 2026', title: 'Offer draft saved', detail: 'Your offer changes were saved for this review session.' }, ...current.activity],
    };
    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }

  submitCase(caseNumber: string, expectedRevision?: number): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((item) => item.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0 || !this.cases[index].capabilities.canSubmit || (expectedRevision !== undefined && expectedRevision !== this.cases[index].savedRevision)) {
      return of(null).pipe(delay(50));
    }

    const current = this.cases[index];
    const updated: PartyCaseOverview = {
      ...current,
      isEditable: false,
      isSubmitted: true,
      status: current.offerPayment.paymentState === 'Paid' ? 'Submitted & Paid' : 'Submitted / Payment Pending',
      nextRequiredAction: current.offerPayment.paymentState === 'Paid' ? null : current.offerPayment.hasOutstandingBalance ? 'Pay outstanding case fees' : 'No action required',
      lineItems: current.lineItems.map((line) => ({ ...line, ownOfferState: line.ownOfferAmount === null ? 'No offer submitted' : 'Submitted' })),
      offerPayment: { ...current.offerPayment, offerState: 'Submitted', submittedLockedState: 'Locked' },
      offerLifecycle: 'submitted',
      savedRevision: current.savedRevision + 1,
      capabilities: {
        ...current.capabilities,
        canEditOffer: false,
        canSubmit: false,
        canPay: current.offerPayment.hasOutstandingBalance && current.offerPayment.paymentState !== 'Pending',
        canRequestClosure: false,
      },
      activity: [{ date: 'September 14, 2026', title: 'Offer submitted', detail: 'The locked offer revision was submitted.' }, ...current.activity],
    };
    this.cases[index] = updated;
    this.reviewRepository.syncPartySubmission(
      updated.forthrightCaseNumber,
      updated.lineItems.map((line) => ({ id: line.id, amountCents: currencyToCents(line.ownOfferAmount) })),
      updated.narrative,
      updated.attributes,
    );
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }

  refreshCase(caseNumber: string): Observable<PartyCaseOverview | null> {
    return this.getCase(caseNumber);
  }

  /** Review mode never reaches a real Pay page; its handoff is ReviewPaymentRedirectService's synthetic one. */
  startCheckout(_caseNumber: string): Observable<PartyCheckoutSession> {
    return throwError(() => new PaymentNotReadyError('Card payment is not available in the review build.'));
  }

  refreshPayment(caseNumber: string, _expectedRevision: number): Observable<PartyCaseOverview | null> {
    return this.refreshCase(caseNumber);
  }

  confirmReviewPayment(caseNumber: string, expectedRevision: number, state: 'paid' | 'pending' | 'election-pending' | 'canceled' | 'failed'): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((item) => item.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0 || this.cases[index].savedRevision !== expectedRevision) return of(null);
    const current = this.cases[index];
    if (state === 'canceled' || state === 'failed') return of(cloneCase(current));
    const eligible = state === 'paid' || state === 'election-pending';
    const paid = state === 'paid';
    const updated: PartyCaseOverview = {
      ...current,
      isEditable: !eligible,
      offerLifecycle: eligible ? 'locked' : current.offerLifecycle,
      savedRevision: current.savedRevision + 1,
      nextRequiredAction: eligible ? 'Submit your locked offer' : 'Payment is processing',
      offerPayment: {
        ...current.offerPayment,
        paymentState: paid ? 'Paid' : state === 'election-pending' ? 'Election accepted; payment pending' : 'Pending',
        amountPaid: paid ? current.offerPayment.totalDue : current.offerPayment.amountPaid,
        amountOutstanding: paid ? '$0.00' : current.offerPayment.amountOutstanding,
        hasOutstandingBalance: !paid,
        latestPaymentAmount: current.offerPayment.totalDue,
        latestPaymentDate: 'September 14, 2026',
        latestPaymentState: paid ? 'Completed' : state === 'election-pending' ? 'Election accepted' : 'Pending',
        paymentMethodOrReference: 'External payment application',
      },
      accountBalance: paid ? { amount: '$0.00', amountSign: null, label: 'No balance' } : current.accountBalance,
      capabilities: { ...current.capabilities, canEditOffer: !eligible, canPay: false, canCheckPayment: !eligible, canSubmit: eligible },
      activity: [
        {
          date: 'September 14, 2026',
          title: paid ? 'Payment confirmed' : state === 'election-pending' ? 'Payment election accepted' : 'Payment status checked',
          detail: paid
            ? 'The external payment application confirmed payment.'
            : state === 'election-pending'
              ? 'The authoritative election state allows submission while payment remains pending.'
              : 'Payment remains pending.',
        },
        ...current.activity,
      ],
    };
    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }

  saveInformationResponse(caseNumber: string, requestId: string, response: string, documents: readonly PartyDocument[], expectedRevision: number): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((item) => item.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0 || this.cases[index].savedRevision !== expectedRevision || response.length > 10000) return of(null);
    const current = this.cases[index];
    const requests = current.informationRequests.map((request) => (request.id === requestId ? { ...request, status: 'Submitted' as const, response, documents: [...documents] } : request));
    const updated = {
      ...current,
      savedRevision: current.savedRevision + 1,
      informationRequests: requests,
      activity: [{ date: 'September 14, 2026', title: 'Information response submitted', detail: 'Your response was recorded independently of the offer.' }, ...current.activity],
    };
    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }
  sendContact(caseNumber: string, subject: string, message: string): Observable<boolean> {
    return of(this.cases.some((item) => item.forthrightCaseNumber === caseNumber.toUpperCase()) && !!subject.trim() && !!message.trim() && message.length <= 2000).pipe(delay(50));
  }
  submitCaseRequest(caseNumber: string, type: 'Settlement' | 'Withdrawal', note: string): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((item) => item.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0 || !note.trim() || note.length > 2000 || this.cases[index].caseRequests.some((request) => request.type === type && request.status === 'Pending review')) return of(null);
    const current = this.cases[index];
    const receipt: PartyCaseRequestReceipt = { id: `REQUEST-${Date.now()}`, type, status: 'Pending review', submittedAt: 'September 14, 2026 at 11:30 AM ET', note };
    const updated = {
      ...current,
      caseRequests: [receipt, ...current.caseRequests],
      activity: [{ date: 'September 14, 2026', title: `${type} request submitted`, detail: 'Forthright will review this request. Case status has not changed.' }, ...current.activity],
    };
    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }
  openDocument(caseNumber: string, documentId: string): Observable<Blob | null> {
    const item = this.cases.find((candidate) => candidate.forthrightCaseNumber === caseNumber.toUpperCase());
    const known = item && [...item.caseDocuments, ...item.lineItems.flatMap((line) => line.ownDocuments)].some((documentItem) => documentItem.id === documentId);
    const text = 'Synthetic authorized document preview. This review-only preview contains no real patient, payment, or case data.';
    return of(known ? new Blob([text], { type: 'text/plain' }) : null).pipe(delay(40));
  }
  /**
   * The review build has no storage behind it, so an upload records the metadata the fixtures already
   * modelled and nothing more. Deliberately not refused the way the API refuses it in fixture mode:
   * these scenarios exist to be walked through offline, and a picker that always failed would break
   * the walkthrough rather than keep it honest.
   */
  uploadDocument(caseNumber: string, file: File, association?: DocumentAssociation): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((candidate) => candidate.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0) return of(null).pipe(delay(40));

    const current = this.cases[index];
    const document: PartyDocument = {
      id: `review-document-${Date.now()}-${this.reviewDocumentSequence++}`,
      name: file.name,
      sizeBytes: file.size,
      mediaType: file.type || 'application/octet-stream',
      revision: 1,
    };

    const updated: PartyCaseOverview = association?.lineItemId
      ? { ...current, lineItems: current.lineItems.map((line) => (line.id === association.lineItemId ? { ...line, ownDocuments: [...line.ownDocuments, document] } : line)) }
      : { ...current, caseDocuments: [...current.caseDocuments, document] };

    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }
  deleteDocument(caseNumber: string, documentId: string): Observable<PartyCaseOverview | null> {
    const index = this.cases.findIndex((candidate) => candidate.forthrightCaseNumber === caseNumber.toUpperCase());
    if (index < 0) return of(null).pipe(delay(40));

    const current = this.cases[index];
    const updated: PartyCaseOverview = {
      ...current,
      caseDocuments: current.caseDocuments.filter((documentItem) => documentItem.id !== documentId),
      lineItems: current.lineItems.map((line) => ({ ...line, ownDocuments: line.ownDocuments.filter((documentItem) => documentItem.id !== documentId) })),
    };

    this.cases[index] = updated;
    this.persist();
    return of(cloneCase(updated)).pipe(delay(50));
  }
  private reviewDocumentSequence = 0;
  private storageName(): string {
    return `party-cases.${this.scenarios.selectedId()}`;
  }
  private initialCasesForScenario(): PartyCaseOverview[] {
    if (this.scenarios.selectedId() === 'empty') return [];
    const cases = initialMockCases.map(cloneCase);
    if (this.scenarios.selectedId() !== 'long-batch') return cases;
    const index = cases.findIndex((item) => item.forthrightCaseNumber === 'NS100241');
    const source = cases[index].lineItems;
    const lineItems = Array.from({ length: 50 }, (_, lineIndex) => ({
      ...source[lineIndex % source.length],
      id: `LI-${lineIndex + 1}`,
      serviceCode: String(99280 + (lineIndex % 6)),
      ownOfferAmount: null,
      ownOfferState: 'Not entered',
      ownDocuments: [],
    }));
    cases[index] = {
      ...cases[index],
      isBatched: true,
      serviceDescription: '50 emergency physician services',
      serviceCodeSummary: '50 service-code line items',
      qpaSummary: '50 line-item QPAs',
      lineItems,
      offerPayment: { ...cases[index].offerPayment, offerAmountOrTotal: null, supportingDocuments: '0 documents' },
    };
    return cases;
  }
  private persist(): void {
    this.session.write('party-org-sunrise', this.storageName(), this.cases);
  }
}

function formatCurrency(amount: number | null): string | null {
  return amount === null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function cloneCase(item: PartyCaseOverview): PartyCaseOverview {
  return {
    ...item,
    accountBalance: { ...item.accountBalance },
    caseDocuments: item.caseDocuments.map((documentItem) => ({ ...documentItem })),
    lineItems: item.lineItems.map((line) => ({ ...line, ownDocuments: line.ownDocuments.map((documentItem) => ({ ...documentItem })) })),
    offerPayment: { ...item.offerPayment },
    deadline: { ...item.deadline },
    determination: item.determination ? { ...item.determination } : null,
    capabilities: { ...item.capabilities },
    attributes: { ...item.attributes },
    informationRequests: item.informationRequests.map((request) => ({ ...request, documents: request.documents.map((documentItem) => ({ ...documentItem })) })),
    activity: item.activity.map((event) => ({ ...event })),
    caseRequests: item.caseRequests.map((request) => ({ ...request })),
  };
}

function currencyToCents(value: string | null): number | null {
  if (value === null) return null;
  const amount = Number(value.replace(/[$,]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

export const PARTY_CASE_SERVICE = new InjectionToken<PartyCaseService>('PartyCaseService');
